<template>
  <div class="wrapper">
    <div class="canvas-top-header" v-loading="loading" element-loading-spinner="atom-audit-loader">
      <div>
        <div class="button-controls" v-if="options.length > 0">
          <el-button plain round @click="loadCanvas" v-if="options.includes('loadCanvas')"><i class="mdi mdi-folder-open" style="margin-right: 4px;"></i>Load Canvas</el-button>

          <el-popover ref="saveCanvasPopover" v-model="saveCanvasVisible" placement="top" width="650" trigger="manual" :title="saveAsNew ? 'Save Canvas as New' : 'Save Canvas'">
            <el-form ref="canvasForm" :model="compCanvasData" :rules="rules" label-width="150px" label-position="left" style="margin-right: 10px;">
              <el-form-item label="Canvas Name" prop="name">
                <el-input v-model="compCanvasData.name" placeholder="Canvas Name" @change="handleChange" />
              </el-form-item>
              <el-form-item label="Canvas Description" prop="description">
                <el-input v-model="compCanvasData.description" placeholder="Canvas Description" type="textarea" :rows="3" @change="handleChange" />
              </el-form-item>
              <el-form-item label="Projects" prop="projectId" style="margin-top: 10px;">
                <ProjectSelector ref="projectSelector" style="display: none;" @selected="addCanvasProject" :defaultQuery="{ projectTypeId: 1002 }" placeholder="Select a project to add" class="project-selector" />

                <div class="projects-list">
                  <el-card class="projects-card">
                    <div style="font-size: 13px; font-weight: 500; margin-bottom: 8px; border-bottom: 1px solid #e0e0e0; padding-bottom: 5px;">Associated Projects</div>
                    <div class="project-list" style="word-break: break-word;">
                      <div v-if="compCanvasData.canvasProjects.length === 0">
                        <small style="font-style: italic;">No associated projects</small>
                      </div>
                      <div class="project-item" v-for="row in compCanvasData.canvasProjects" :key="row.projectId">
                        <span>{{ row.project ? row.project.projectID : null }}</span>
                        <el-button type="text" size="mini" style="margin-left: 10px;" @click="deleteCanvasProject(row.projectId)">Remove</el-button>
                      </div>
                      <el-button plain round style="height: 22px; padding: 4px 15px; margin-top: 15px;" size="mini" @click="openProjectSelector()">
                        Add Project
                      </el-button>
                    </div>
                  </el-card>
                </div>
              </el-form-item>
            </el-form>
            <center>
              <el-button-group style="margin-top: 20px;">
                <el-button plain round @click="saveCanvas(false)"><i class="mdi mdi-content-save" style="margin-right: 4px;"></i>Save</el-button>
                <el-button plain round @click="handleCancelPopover"><i class="mdi mdi-close" style="margin-right: 4px;"></i>Cancel</el-button>
              </el-button-group>
            </center>
          </el-popover>

          <template v-if="options.includes('saveCanvas')">
            <el-button ref="saveCanvasButton" :disabled="canvasStatus === 'New'" v-if="canvasStatus === 'New' || canvasStatus === 'Unsaved'" plain round style="margin-left: 10px;" @click="openCanvasPopover(false)"><i class="mdi mdi-content-save" style="margin-right: 4px;"></i>Save Canvas</el-button>
            <el-dropdown v-else id="saveCanvasDropdown" ref="saveCanvasButton" split-button plain round style="margin-left: 10px;" @click="openCanvasPopover(false)" @command="handleSaveAs"
              ><i class="mdi mdi-content-save" style="margin-right: 4px;"></i>Save Canvas
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="saveAsNew">Save Canvas as New</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>

          <el-button style="margin-left: 10px;" plain round :disabled="canvasStatus === 'New'" @click="clearCanvas" v-if="options.includes('clearCanvas')"><i class="mdi mdi-close" style="margin-right: 4px;"></i>Clear Canvas</el-button>
          <el-button style="margin-left: 10px;" plain round :disabled="canvasStatus === 'New'" @click="deleteCanvas" v-if="options.includes('deleteCanvas')"><i class="mdi mdi-delete" style="margin-right: 4px;"></i>Delete Canvas</el-button>
          <el-button style="margin-left: 10px;" plain round :disabled="canvasStatus === 'New'" @click="handleCreateReport" v-if="options.includes('createReport')" icon="el-icon-document">Create Report</el-button>
          <el-button style="margin-left: 10px;" plain round @click="download" v-if="options.includes('download')"><i class="mdi mdi-file-download" style="margin-right: 4px;"></i>Download</el-button>
        </div>

        <div style="font-size: 13px;">
          <template v-if="canvasStatus !== 'New'">
            <div class="canvas-info-flex">
              <div class="info-row print-hidden">
                <div class="info-group">
                  <span class="info-label">Canvas Name</span>
                  <span class="info-value" :style="{ fontStyle: !compCanvasData.name ? 'italic' : 'normal' }">
                    {{ compCanvasData.name || 'Untitled canvas' }}
                  </span>
                </div>
                <div class="info-group" v-if="compCanvasData.createdDate">
                  <span class="info-label">Created Date</span>
                  <span class="info-value">
                    {{ compCanvasData.createdDate ? format(parseISO(compCanvasData.createdDate), 'MM/dd/yyyy hh:mm a') : '' }}
                  </span>
                </div>
              </div>
              <div class="info-row description-row">
                <div class="info-group full-width">
                  <span class="info-label print-hidden">Description</span>
                  <span id="canvasDescription" class="info-value" :style="{ fontStyle: !compCanvasData.description ? 'italic' : 'normal' }">
                    {{ compCanvasData.description || 'None' }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
      <div>
        <el-form id="displayControls" :class="{ opacityForm: compCanvasData.canvasRows.length === 0 }" :disabled="compCanvasData.canvasRows.length === 0" label-width="100px" label-position="left" style="margin-right: 10px;">
          <el-form-item label="Display Mode">
            <el-select v-model="compCanvasData.displayMode" placeholder="Display Mode" size="mini" @change="handleAutoSave">
              <el-option label="Separate" value="separate" />
              <el-option label="Overlay" value="overlay" />
              <el-option label="Aggregate" value="aggregate" />
            </el-select>
            <div style="margin: 10px 0px 0px 0px;font-size: 13px;">
              <input type="checkbox" v-model="compCanvasData.medicareCompare" @change="handleAutoSave" />
              Medicare Compare <span style="font-size: 20px;color:#fee18f;margin: -1px 0px 0px 3px;position:absolute;">●</span>
            </div>
            <div style="font-size: 13px;">
              <input type="checkbox" v-model="compCanvasData.specialtyCompare" @change="handleAutoSave" />
              Provider Specialty Compare <span style="font-size: 20px;color:#f57fc5;margin: -1px 0px 0px 3px;position:absolute;">●</span>
            </div>
            <div style="font-size: 13px;">
              <input type="checkbox" v-model="compCanvasData.providerSpecialtiesCompare" @change="handleAutoSave" />
              Provider Specialties Compare <span style="font-size: 20px;color:#7cbe5f;margin: -1px 0px 0px 3px;position:absolute;">●</span>
            </div>
            <div style="margin-top: 10px; margin-left: 5px;display: flex;" v-show="compCanvasData.providerSpecialtiesCompare">
              <div class="specialtiesFilterContainer">
                <span v-if="!compCanvasData.providerSpecialtiesSelect.length"><i style="margin: 0px 5px 0px 5px;font-size: 14px; font-weight: 100; color: #b4bccc;" class="el-icon-star-off"></i><span style="color: #b4bccc;">Specialties</span></span>
                <span v-html="compTitle"></span>
              </div>
              <div class="specialtiesFilterButton">
                <el-button size="mini" title="add values" icon="el-icon-search" @click="openSpecialtiesCompare()"></el-button>
              </div>
            </div>
          </el-form-item>
        </el-form>
      </div>
    </div>
    <div v-if="compCanvasData.canvasRows.length === 0">
      <div v-if="!isDashboard">
        Nothing added to canvas. From the other Pro Risk Analysis tabs, click the Add to Canvas button to add items to the canvas or click Load Canvas to load a existing canvas from the database.
      </div>
      <div v-else>
        Nothing added to canvas. Click Load Canvas to load a canvas from the database.
      </div>
    </div>

    <div v-else id="canvas-container">
      <!-- <h4>{{ compCanvasData.description }}</h4> -->
      <el-row v-for="(headerRow, index) in calcTableRows" :key="index" class="canvas-row-category">
        <el-col :span="24" class="canvas-row-header">
          <h4 class="canvas-description">{{ headerRow.emCategory }}; Claims from {{ headerRow.startDate }} to {{ headerRow.endDate }}</h4>
        </el-col>
        <el-row v-for="(row, index) in headerRow.canvasRows" :key="index" class="canvas-row">
          <el-col :span="fullWidth(row.seriesData) ? 24 : 12" :class="{ hiaTable: !isPrintable }">
            <FrequencyTable :seriesData="row.seriesData" :showAllSeries="compCanvasData.displayMode !== 'separate'" :medicareCompare="compCanvasData.medicareCompare" :specialtyCompare="compCanvasData.specialtyCompare" :providerSpecialtiesCompare="compCanvasData.providerSpecialtiesCompare && compCanvasData.providerSpecialtiesSelect.length > 0"></FrequencyTable>
          </el-col>
          <el-col :span="fullWidth(row.seriesData) ? 24 : 12" :style="{ marginTop: fullWidth(row.seriesData) ? '20px' : '0px' }">
            <div class="chart-container">
              <ProRiskAnalysisChart :height="isPrintable ? 265 : 200" :seriesData="row.seriesData" :showAllSeries="compCanvasData.displayMode !== 'separate'" :medicareCompare="compCanvasData.medicareCompare" :specialtyCompare="compCanvasData.specialtyCompare" :providerSpecialtiesCompare="compCanvasData.providerSpecialtiesCompare && compCanvasData.providerSpecialtiesSelect.length > 0" type="canvas" :providerName="row.canvasName"></ProRiskAnalysisChart>
              <el-button v-if="!isDashboard && compCanvasData.displayMode === 'separate'" plain round size="small" @click="removeFromCanvas(index)" class="remove-button"><i class="mdi mdi-delete" style="margin-right: 4px;"></i>Remove</el-button>
            </div>
          </el-col>
        </el-row>
      </el-row>
    </div>

    <el-dialog title="Select Canvas" :visible.sync="loadCanvasVisible" width="80%">
      <ProRiskCanvasList v-if="loadCanvasVisible" @select="selectCanvas" />
    </el-dialog>

    <SpecialtiesCompareDialog v-if="showSpecialtiesCompareModal" :tile="'Select Specialties to Compare'" :showModal="showSpecialtiesCompareModal" @closeModal="handleCloseSpecialtiesModal" :specialties="specialtiesSelectList" @applySpecialties="handleApplySpecialtiesCompare" :providerSpecialtiesSelect="compCanvasData.providerSpecialtiesSelect" :propertyValue="'providerTaxonomy'" :loading="filterDataLoading"> </SpecialtiesCompareDialog>
  </div>
</template>

<script>
  import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
  import FrequencyTable from './FrequencyTable.vue'
  import ProRiskAnalysisChart from './ProRiskAnalysisChart.vue'
  import { parseISO, format } from 'date-fns'
  import SpecialtiesCompareDialog from './SpecialtiesCompareDialog.vue'
  import ProjectSelector from '@/components/controls/projectSelector.vue'
  import ProRiskCanvasList from './ProRiskCanvasList.vue'

  export default {
    name: 'ProRiskCanvas',
    components: {
      FrequencyTable,
      ProRiskAnalysisChart,
      SpecialtiesCompareDialog,
      ProjectSelector,
      ProRiskCanvasList
    },
    props: {
      options: {
        type: Array,
        required: false,
        default: () => ['loadCanvas', 'deleteCanvas', 'saveCanvas', 'createReport', 'clearCanvas']
      },
      isDashboard: {
        type: Boolean,
        required: false,
        default: false
      },
      isPrintable: {
        type: Boolean,
        required: false,
        default: false
      },
      propCanvas: {
        type: Object,
        required: false,
        default: null
      }
    },
    data() {
      return {
        parseISO,
        format,
        loading: false,
        canvasListLoading: false,
        showSpecialtiesCompareModal: false,
        filterDataLoading: false,
        saveCanvasVisible: false,
        saveAsNew: false,
        isDirty: false,
        popoverBackup: {
          name: '',
          description: '',
          canvasProjects: []
        },
        canvasListOptions: {
          headings: {
            name: 'Name',
            description: 'Description',
            createdBy: 'Created By',
            createdDate: 'Created Date',
            project: 'Project',
            delete: ''
          }
        },
        rules: {
          name: [{ required: true, message: 'Please enter a canvas name', trigger: 'manual' }],
          description: [{ required: true, message: 'Please enter a canvas description', trigger: 'manual' }]
        },
        seriesColors: [
          '#7f98b4', // Original blue
          '#4CAF50', // Green
          '#FF9800', // Orange
          '#9C27B0', // Purple
          '#E91E63', // Pink
          '#00BCD4', // Cyan
          '#FFC107', // Amber
          '#795548' // Brown
        ],
        loadCanvasVisible: false,
        canvasColumns: [
          { label: 'Name', field: 'name' },
          { label: 'Description', field: 'description' }
        ]
      }
    },
    async mounted() {
      if (this.$route.query.canvasId) {
        this.loading = true
        try {
          await this.GET_CANVAS_BY_ID(this.$route.query.canvasId)
        } catch (err) {
          this.$message.error('Failed to load canvas: ' + err.message)
        } finally {
          this.loading = false
        }
      }
    },
    methods: {
      ...mapActions('proReviews/', ['GET_SPECIALTIES_FOR_FILTER', 'LOAD_SPECIALTIES_AGGEGATED_DISTRIBUTION']),
      ...mapActions('proReviews/canvas/', ['SAVE_CANVAS', 'GET_CANVAS_LIST', 'GET_CANVAS_BY_ID', 'DELETE_CANVAS']),
      ...mapMutations('proReviews/canvas/', ['CLEAR_CANVAS_DATA', 'SET_CANVAS_DATA', 'SET_CANVAS_SPECIALTIES_SELECT_LIST', 'CLEAR_CANVAS_DATA', 'UPDATE_PROJECT_CANVAS_LIST']),
      async openCanvasPopover(saveAsNew = false) {
        this.popoverBackup = {
          name: this.compCanvasData.name,
          description: this.compCanvasData.description,
          canvasProjects: [...this.compCanvasData.canvasProjects]
        }

        this.saveAsNew = saveAsNew
        if (saveAsNew) {
          this.compCanvasData.name = `Copy of ${this.compCanvasData.name}`
          this.compCanvasData.description = `Copy of ${this.compCanvasData.description}`
        }

        const form = this.$refs.canvasForm
        if (form) {
          await form.clearValidate()
        }

        const popover = this.$refs.saveCanvasPopover
        popover.doDestroy()
        popover.referenceElm = this.$refs.saveCanvasButton.$el
        popover.doShow()
      },
      handleCancelPopover() {
        if (this.hasUnsavedChanges()) {
          this.$confirm('You have unsaved changes. Are you sure you want to leave?').then(() => {
            this.saveCanvasVisible = false
            this.compCanvasData.name = this.popoverBackup.name
            this.compCanvasData.description = this.popoverBackup.description
            this.compCanvasData.canvasProjects = [...this.popoverBackup.canvasProjects]
          })
        } else {
          this.saveCanvasVisible = false
        }
      },
      handleSaveAs(command) {
        if (command === 'saveAsNew') {
          this.openCanvasPopover(true)
        }
      },
      fullWidth(seriesData) {
        let seriesCount = seriesData.filter(series => series.seriesType === 'category').length

        if (this.compCanvasData.medicareCompare) {
          seriesCount += 1
        }
        if (this.compCanvasData.specialtyCompare) {
          seriesCount += 1
        }
        if (this.compCanvasData.providerSpecialtiesCompare) {
          seriesCount += 1
        }

        return seriesCount > 2
      },
      clearCanvas() {
        this.$confirm('Are you sure you want to clear the Canvas?').then(() => {
          this.CLEAR_CANVAS_DATA()
          this.isDirty = false
        })
      },
      openProjectSelector() {
        this.$refs.projectSelector.searchReviewProjects()
      },
      addCanvasProject(project) {
        if (this.compCanvasData.canvasProjects.some(c => c.projectId === project.id)) {
          this.$message.error('Canvas already added to project')
          return
        }

        this.compCanvasData.canvasProjects.push({ projectId: project.id, canvasId: this.compCanvasData.canvasId, project: project })
        this.handleChange()
      },
      async deleteCanvasProject(projectId) {
        this.compCanvasData.canvasProjects = this.compCanvasData.canvasProjects.filter(row => row.projectId !== projectId)
        this.handleChange()
      },
      handleAutoSave() {
        if (this.compCanvasData.canvasId > 0 && !this.isDashboard) {
          const autoSave = true
          this.saveCanvas(autoSave)
        }
        // if the canvas is loaded into a project dashboard, then update the state so that any changes are reflected in the dashboard
        this.UPDATE_PROJECT_CANVAS_LIST(this.compCanvasData)
      },
      async saveCanvas(autoSave = false) {
        try {
          await this.$refs.canvasForm.validate()
        } catch (error) {
          console.log(error)
          return
        }

        try {
          if (!autoSave) {
            this.loading = true
          }

          const payload = {
            ...this.compCanvasData,
            autoSave: autoSave,
            providerSpecialtiesSelect: JSON.stringify(this.compCanvasData.providerSpecialtiesSelect),
            canvasRows: JSON.stringify(this.compCanvasData.canvasRows)
          }

          if (this.saveAsNew) {
            payload.canvasId = 0
            payload.canvasProjects.forEach(proj => {
              proj.Id = 0
            })
          }

          await this.SAVE_CANVAS(payload)
        } catch (error) {
          if (error.message) {
            this.$message.error('Failed to save canvas: ' + error.message)
          }
        } finally {
          this.loading = false
          this.saveCanvasVisible = false
          this.saveAsNew = false
          this.isDirty = false
          this.$emit('dirty', this.isDirty)
        }
      },
      hasUnsavedChanges() {
        return this.isDirty || this.canvasStatus === 'Unsaved'
      },
      async loadCanvas() {
        if (this.hasUnsavedChanges()) {
          this.$confirm('You have unsaved changes. Are you sure you want to load a new canvas?').then(() => {
            this.loadCanvasVisible = true
          })
        } else {
          this.loadCanvasVisible = true
        }
      },
      selectCanvas(canvas) {
        if (this.isDashboard) {
          this.propCanvas = canvas
        } else {
          this.SET_CANVAS_DATA(canvas)
          if (this.$route.query.canvasId) {
            this.$router.replace({ query: {} })
          }
        }

        if (this.compCanvasData.providerSpecialtiesCompare && this.specialtiesSelectList.length === 0) {
          try {
            this.filterDataLoading = true
            const dateRange = this.GET_DATERANGE()
            const payload = {
              dateRange: dateRange,
              category: this.emCategorySelect
            }
            this.GET_SPECIALTIES_FOR_FILTER(payload).then(response => {
              this.SET_CANVAS_SPECIALTIES_SELECT_LIST(response)
              this.filterDataLoading = false
            })
          } catch (err) {
            console.log(err)
            this.filterDataLoading = false
          }
        }
        this.loadCanvasVisible = false
      },
      async removeFromCanvas(index) {
        try {
          await this.$confirm('Are you sure you want to remove this row from the canvas?', 'Warning', {
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            type: 'warning'
          })
        } catch (err) {
          // just return if the user cancelled
          return
        }
        this.compCanvasData.canvasRows.splice(index, 1)
        this.handleAutoSave()
      },
      async deleteCanvas() {
        try {
          await this.$confirm(`Are you sure you want to delete canvas "${this.compCanvasData.name}"?`, 'Warning', {
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            type: 'warning'
          })
        } catch (err) {
          // just return if the user cancelled
          return
        }

        try {
          await this.DELETE_CANVAS(this.compCanvasData.canvasId)
          this.CLEAR_CANVAS_DATA()
        } catch (err) {
          this.$message.error('Failed to delete canvas: ' + err.message)
        } finally {
          this.$message.success('Canvas deleted successfully')
        }
      },
      getUniqueSeries(seriesType, emCategory, startDate, endDate) {
        const matches = []
        this.compCanvasData.canvasRows.forEach(row => {
          row.seriesData.forEach(series => {
            if (series.seriesType === seriesType && row.emCategory === emCategory && row.startDate === startDate && row.endDate === endDate) {
              if (!matches.some(match => match.seriesTitle === series.seriesTitle)) {
                matches.push(series)
              }
            }
          })
        })
        return matches
      },
      getMinMaxDate(canvasRows) {
        const minDate = Math.min(...canvasRows.map(row => new Date(row.startDate)))
        const maxDate = Math.max(...canvasRows.map(row => new Date(row.endDate)))
        return {
          startDate: format(minDate, 'yyyy-MM-dd'),
          endDate: format(maxDate, 'yyyy-MM-dd')
        }
      },
      openSpecialtiesCompare() {
        if (this.specialtiesSelectList.length === 0) {
          try {
            this.filterDataLoading = true
            const dateRange = this.getMinMaxDate(this.compCanvasData.canvasRows)
            const payload = {
              dateRange: dateRange,
              category: this.emCategorySelect
            }
            Promise.all([this.GET_SPECIALTIES_FOR_FILTER(payload)]).then(response => {
              this.SET_CANVAS_SPECIALTIES_SELECT_LIST(response[0])
              this.filterDataLoading = false
            })
          } catch (err) {
            console.log(err)
            this.filterDataLoading = false
          }
        }

        this.showSpecialtiesCompareModal = true
      },
      handleCloseSpecialtiesModal() {
        this.showSpecialtiesCompareModal = false
      },
      async handleApplySpecialtiesCompare(e) {
        this.loading = true
        const uniqueCombinations = new Map()

        // Build map of unique combinations
        for (const row of this.compCanvasData.canvasRows) {
          const key = `${row.startDate}|${row.endDate}|${row.emCategory}`
          if (!uniqueCombinations.has(key)) {
            uniqueCombinations.set(key, {
              startDate: row.startDate,
              endDate: row.endDate,
              emCategory: row.emCategory
            })
          }
        }

        // only call the server for each unique combination of startDate, endDate, and emCategory
        const promises = []
        uniqueCombinations.forEach(params => {
          const promise = this.LOAD_SPECIALTIES_AGGEGATED_DISTRIBUTION({
            dateRange: { startDate: params.startDate, endDate: params.endDate },
            category: params.emCategory,
            specialties: e
          }).then(distribution => {
            // update the matching canvas rows with the distributions from the server
            const canvasRows = this.compCanvasData.canvasRows.filter(row => row.startDate === params.startDate && row.endDate === params.endDate && row.emCategory === params.emCategory)
            canvasRows.forEach(canvasRow => {
              const existingSpecialtiesIndex = canvasRow.seriesData.findIndex(item => item.seriesType === 'specialties')
              if (existingSpecialtiesIndex !== -1) {
                canvasRow.seriesData.splice(existingSpecialtiesIndex, 1, distribution)
              } else {
                canvasRow.seriesData = canvasRow.seriesData.concat(distribution)
              }
            })
          })
          promises.push(promise)
        })

        try {
          await Promise.all(promises)
        } catch (err) {
          this.$message.error('Failed to load specialties compare distributions: ' + err.message)
        } finally {
          this.loading = false
          this.handleAutoSave()
        }

        this.showSpecialtiesCompareModal = false
        this.compCanvasData.providerSpecialtiesSelect = e
      },
      handleCreateReport() {
        window.localStorage.removeItem('canvasProjectId')
        window.localStorage.setItem('canvasList', JSON.stringify(this.compCanvasData))
        window.open(`/reports/pro-risk-canvas-printable?fullscreen=true&dt=${new Date().getTime()}&useLocalCanvas=true`, 'Pro Risk Analysis Report', 'height=850,width=1175')
      },
      download() {
        window.print()
      },
      handleChange() {
        this.isDirty = true
        this.$emit('change', this.compCanvasData)
        this.$emit('dirty', this.isDirty)
      }
    },
    computed: {
      ...mapState('proReviews/canvas/', ['canvasData', 'specialtiesSelectList', 'canvasList']),
      ...mapGetters('globals/', ['GET_DATERANGE']),
      canvasStatus() {
        switch (this.compCanvasData.canvasId) {
          case -1:
            return 'New'
          case 0:
            return 'Unsaved'
          default:
            return 'Saved'
        }
      },
      compCanvasData() {
        if (this.propCanvas && Object.keys(this.propCanvas).length > 0) {
          return this.propCanvas
        }
        return this.canvasData
      },
      compTitle() {
        let response = ''
        if (this.compCanvasData.providerSpecialtiesSelect) {
          this.specialtiesSelectList.forEach((v, i) => {
            if (this.compCanvasData.providerSpecialtiesSelect.some(s => s === v.providerTaxonomy)) {
              response += `<span style="display: block; margin-bottom: 3px; width: fit-content;" class="el-tag el-tag--info el-tag--mini el-tag--light"><span class="el-select__tags-text">${v.providerSpecialty ? v.providerSpecialty : v.providerClassification}</span></span>`
            }
          })
        }
        return response
      },
      calcTableRows() {
        // build an outer layer of EM categories and DOS ranges to build the title blocks
        const finalMap = new Map()

        // Build map of unique combinations
        for (const row of this.compCanvasData.canvasRows) {
          const key = `${row.startDate}|${row.endDate}|${row.emCategory}`
          if (!finalMap.has(key)) {
            finalMap.set(key, {
              startDate: row.startDate,
              endDate: row.endDate,
              emCategory: row.emCategory,
              canvasRows: []
            })
          }
        }

        switch (this.compCanvasData.displayMode) {
          case 'overlay': {
            finalMap.forEach(category => {
              const matchingRows = this.compCanvasData.canvasRows.filter(row => row.emCategory === category.emCategory && row.startDate === category.startDate && row.endDate === category.endDate)

              const overlay = {
                canvasName: 'Overlay For ' + matchingRows.map(row => row.canvasName).join(', '),
                seriesData: matchingRows.map((row, index) => {
                  const providerRow = row.seriesData[0]
                  return {
                    ...providerRow,
                    seriesTitle: `${row.canvasName} - ${providerRow.seriesTitle}`,
                    seriesShortTitle: `${row.canvasName}`,
                    color: this.seriesColors[index % this.seriesColors.length]
                  }
                })
              }

              if (this.compCanvasData.medicareCompare) {
                const medicareSeries = this.getUniqueSeries('medicare', category.emCategory, category.startDate, category.endDate)
                medicareSeries.forEach(series => {
                  overlay.seriesData.push({
                    ...series,
                    seriesShortTitle: 'Medicare'
                  })
                })
              }

              if (this.compCanvasData.specialtyCompare) {
                const specialtySeries = this.getUniqueSeries('mySpecialty', category.emCategory, category.startDate, category.endDate)
                specialtySeries.forEach(series => {
                  overlay.seriesData.push({
                    ...series,
                    seriesShortTitle: series.seriesTitle
                  })
                })
              }

              if (this.compCanvasData.providerSpecialtiesCompare) {
                const providerSpecialtiesSeries = this.getUniqueSeries('specialties', category.emCategory, category.startDate, category.endDate)
                providerSpecialtiesSeries.forEach(series => {
                  overlay.seriesData.push({
                    ...series,
                    seriesShortTitle: series.seriesTitle
                  })
                })
              }

              category.canvasRows.push(overlay)
            })
            break
          }
          case 'aggregate': {
            finalMap.forEach(category => {
              const matchingRows = this.compCanvasData.canvasRows.filter(row => row.emCategory === category.emCategory && row.startDate === category.startDate && row.endDate === category.endDate)

              const firstSeries = matchingRows.map(row => row.seriesData[0])

              const aggregatedFrequencies = firstSeries[0].labels.map((_, labelIndex) => {
                return firstSeries.reduce((sum, series) => sum + series.frequencies[labelIndex], 0)
              })

              const total = aggregatedFrequencies.reduce((sum, freq) => sum + freq, 0)
              const aggregatedPercentages = aggregatedFrequencies.map(freq => (freq / total) * 100)

              const aggregatedSeries = {
                canvasName: 'Aggregated For ' + matchingRows.map(row => row.canvasName).join(', '),
                seriesData: [
                  {
                    seriesType: firstSeries[0].seriesType,
                    labels: firstSeries[0].labels,
                    frequencies: aggregatedFrequencies,
                    percentages: aggregatedPercentages,
                    seriesTitle: firstSeries[0].seriesTitle,
                    seriesShortTitle: 'Aggregated'
                  }
                ]
              }

              if (this.compCanvasData.medicareCompare) {
                const medicareSeries = this.getUniqueSeries('medicare', category.emCategory, category.startDate, category.endDate)
                medicareSeries.forEach(series => {
                  aggregatedSeries.seriesData.push({
                    ...series,
                    seriesShortTitle: 'Medicare'
                  })
                })
              }

              if (this.compCanvasData.specialtyCompare) {
                const specialtySeries = this.getUniqueSeries('mySpecialty', category.emCategory, category.startDate, category.endDate)
                specialtySeries.forEach(series => {
                  aggregatedSeries.seriesData.push({
                    ...series,
                    seriesShortTitle: series.seriesTitle
                  })
                })
              }

              if (this.compCanvasData.providerSpecialtiesCompare) {
                const providerSpecialtiesSeries = this.getUniqueSeries('specialties', category.emCategory, category.startDate, category.endDate)
                providerSpecialtiesSeries.forEach(series => {
                  aggregatedSeries.seriesData.push({
                    ...series,
                    seriesShortTitle: series.seriesTitle
                  })
                })
              }

              category.canvasRows.push(aggregatedSeries)
            })
            break
          }
          case 'separate': {
            finalMap.forEach(category => {
              category.canvasRows = this.compCanvasData.canvasRows.filter(row => row.emCategory === category.emCategory && row.startDate === category.startDate && row.endDate === category.endDate)
            })
            break
          }
          default:
            break
        }

        return Array.from(finalMap.values())
      }
    }
  }
</script>

<style scoped>
  >>> .specialtiesCompareBadge button {
    display: block !important;
  }

  >>> .specialtiesCompareBadge > .el-badge__content.is-fixed {
    top: 5px;
    right: 15px;
  }

  >>> .specialtiesFilterContainer {
    width: 90%;
    float: left;
    border: 1px solid #dcdfe6;
    overflow-x: hidden;
    border-radius: 4px;
    padding: 3px 5px;
    font-size: 12px;
    color: #606266;
    line-height: 1.834;
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
    display: flex;
  }

  >>> .specialtiesFilterButton {
    float: left;
  }

  >>> .specialtiesFilterButton button {
    width: 100%;
    height: 30px;
    border-radius: 4px;
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
    border-left: 0px transparent;
  }

  >>> .specialtiesFilterButton button > .el-icon-search {
    color: #c0c4cc;
  }

  .canvas-top-header {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    justify-content: space-between;
    background-color: #f8f9fa;
    padding: 15px 15px 15px 15px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    margin-bottom: 20px;
  }

  .canvas-top-header .el-form {
    margin: 0;
  }

  .button-controls {
    margin-bottom: 20px;
  }

  .canvas-info-flex {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0px 0px 0px 10px;
  }

  .info-group {
    display: flex;
    flex-direction: row;
    gap: 8px;
    min-width: 220px;
    align-items: baseline;
  }

  .info-group.full-width {
    width: 100%;
  }

  .info-row {
    display: flex;
    gap: 42px;
    align-items: baseline;
  }

  .description-row {
    margin-top: 4px;
  }

  .info-label {
    color: #606266;
    font-size: 13px;
    font-weight: 600;
    min-width: 90px;
    text-align: left;
  }

  .info-value {
    font-size: 13px;
    max-width: 400px;
    text-align: left;
    word-break: break-word;
  }

  .info-group.full-width .info-value {
    max-width: none;
  }

  .canvas-header {
    border-top: 1px solid #e0e0e0;
    background-color: #f8f9fa;
    margin-bottom: 20px;
  }

  .canvas-description {
    font-size: 14px;
    margin-bottom: 10px;
    color: #606266;
    font-weight: bold;
    border-radius: 4px;
    padding-bottom: 5px;
  }

  .canvas-row-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 15px;
    margin-bottom: 20px;
    background-color: #fdfdfd;
    border-bottom: 1px solid #ebeef5;
  }

  .canvas-row-category:not(:first-child) {
    margin-top: 40px;
  }

  .canvas-row:not(:first-child) {
    margin-top: 20px;
  }

  .canvas-row-header h4 {
    margin: 0;
  }

  .opacityForm {
    opacity: 0.5;
  }

  #saveCanvasDropdown >>> button:first-child {
    border-top-left-radius: 20px !important;
    border-bottom-left-radius: 20px !important;
  }

  #saveCanvasDropdown >>> button:last-child {
    border-top-right-radius: 20px !important;
    border-bottom-right-radius: 20px !important;
  }

  .chart-container {
    position: relative;
  }

  .remove-button {
    position: absolute;
    top: 15px;
    right: 10px;
    z-index: 1;
  }

  .project-selector {
    flex: 1;
  }

  .projects-list {
    margin-top: 5px;
  }

  .projects-header {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .project-count {
    color: #909399;
    font-size: 0.9em;
  }
</style>

<style scoped>
  @media print {
    #displayControls {
      display: none !important;
    }

    .button-controls {
      display: none !important;
    }

    #canvas-container {
      visibility: visible !important;
      /* max-width: 1000px !important; */
    }

    #canvas-container * {
      display: block !important;
      visibility: visible !important;
    }

    .canvas-row {
      margin-bottom: 20px !important;
    }

    .canvas-row-header {
      margin-bottom: 10px !important;
    }

    #canvasDescription {
      display: block !important;
      font-size: 18px !important;
      font-weight: bold !important;
    }

    .print-hidden {
      display: none !important;
    }

    /* #canvasDescription + .info-value {
      display: block !important;
      font-size: 16px !important;
      margin-bottom: 30px !important;
    } */

    /* .canvas-row {
      break-after: page;
      page-break-after: always;
    } */
  }
</style>
